import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Form, Input, notification, Skeleton, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useIndicators } from 'features/indicators';
import { DayOffsStatusesMap, DayOffsStatusTypesMap, TPreparedDayOffPublicated } from 'shared/types/dayoff.types';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import { VacationModal } from 'features/vacations/vacationModal';
import {
  useApproveDayoffMutation,
  useLazySingleDayoffQuery,
  useRejectDayoffMutation,
} from 'app/services/edpApiV1/dayoff';
import { dataForDayOffUserTables } from 'shared/helpers/utils';
import { useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import style from './index.module.scss';

/**
 * @page DayOff user
 */

const columns: ColumnsType<TPreparedDayOffPublicated> = [
  {
    title: '№',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Дата',
    dataIndex: 'created',
    key: 'created',
  },
  {
    title: 'ФИО',
    dataIndex: 'fio',
    key: 'fio',
  },

  {
    title: 'Дата начала',
    dataIndex: 'dayOffDate',
    key: 'dayOffDate',
  },
  {
    title: 'Дата окончания',
    dataIndex: 'endedAt',
    key: 'endedAt',
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    render: (status: string, record) => {
      return (
        <>
          <span>
            <Badge className={style.badge} status={DayOffsStatusTypesMap[status]} />
            {DayOffsStatusesMap[status]}
          </span>
          <br />
          {/* {!status.isSupervisorApproved && <SuperviserApproveMarker text="Не согласовано руководителем" />} */}
        </>
      );
    },
  },
];

const DayOffUser = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const breadCrumbs = [
    { name: 'Отгулы', link: '/dayoff' },
    { name: 'Отгул', link: `/dayoffUser/${id}` },
  ];

  const [dayoffTrigger, { data, isLoading, isFetching, isSuccess }] = useLazySingleDayoffQuery();
  const [reject, { isLoading: isRejectLoading }] = useRejectDayoffMutation();
  const [approve, { isLoading: isApproveLoading }] = useApproveDayoffMutation();

  useEffect(() => {
    if (id) {
      dayoffTrigger({ id: +id, allAvailableOrganizations });
    }
  }, [id, allAvailableOrganizations]);

  useEffect(() => {
    if (data) {
      form.setFields([
        {
          name: 'tabel',
          value: data?.employee?.personnelNumber,
        },
        {
          name: 'division',
          value: data?.employee?.division,
        },
        {
          name: 'position',
          value: data?.employee?.position,
        },
      ]);
    }
  }, [data, form]);

  useIndicators(isSuccess, isFetching);

  const applyDayOff = () => {
    form.submit();
  };

  const openRejectModal = () => {
    setRejectModalOpen(true);
  };

  const handleSubmitRejectModal = useCallback(
    async (text: string) => {
      try {
        await reject({ rejectionReason: text, requestId: data?.id }).unwrap();
        navigate('/dayoff');
      } catch (e: any) {
        notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
      }
    },
    [data, reject],
  );
  const onSubmit = async () => {
    if (!data) {
      notification.error({ message: 'Нет информации по текущему отгулу' });
      return;
    }
    try {
      await approve(data.id).unwrap();
      navigate('/dayoff');
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  return (
    <>
      <h3>
        <Breadcrumbs breadCrumbs={breadCrumbs} />
      </h3>
      <div className={style.wrap}>
        {isLoading ? (
          <Skeleton active />
        ) : (
          <div className={style.wrapper}>
            <div className={style.buttons}>
              <Button htmlType="submit" onClick={() => openRejectModal()} loading={isRejectLoading}>
                Отклонить
              </Button>
              <Button htmlType="submit" type="primary" onClick={applyDayOff} loading={isApproveLoading}>
                Согласовать
              </Button>
            </div>
            <Table
              scroll={{ x: true }}
              dataSource={dataForDayOffUserTables(data ? [data] : [])}
              columns={columns}
              pagination={false}
              bordered
            />

            <Form form={form} layout="vertical" name="userInfo" className={style.userInfo} onFinish={onSubmit}>
              <div className={style.groupItem}>
                <Form.Item name="tabel" label="Табельный номер" labelAlign="left" className={style.inputWrap}>
                  <Input className="userInput" disabled />
                </Form.Item>
                <Form.Item name="division" label="Подразделение" labelAlign="left" className={style.inputWrap}>
                  <Input className="userInput" disabled />
                </Form.Item>
              </div>
              <div className={style.groupItem}>
                <Form.Item name="position" label="Должность" labelAlign="left" className={style.inputWrap}>
                  <Input className="userInput" disabled />
                </Form.Item>
              </div>
            </Form>
          </div>
        )}
      </div>
      <VacationModal
        id="1"
        openModal={rejectModalOpen}
        setOpenModal={setRejectModalOpen}
        handleSubmit={handleSubmitRejectModal}
        loading={isRejectLoading}
      />
    </>
  );
};

export default DayOffUser;
